/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import {
  has,
  includes,
  isNumber,
  isObject,
  isString,
  kebabCase,
  keyBy,
  map,
  mapValues,
  toNumber,
  without,
} from 'lodash';
import { ColumnSortDirection } from '../../components/listing/types';

const PAGE_SIZES = [25, 50, 100, 200];

export function isOldUrlSyntax(objectTypeRaw: string, objectTypeMetaOldSyntax): boolean {
  return has(objectTypeMetaOldSyntax, objectTypeRaw);
}

export function translateOldUrlSyntax(to: Route, objectTypeMeta, attributeMeta): object {
  const backendKeyToName = mapValues(keyBy(attributeMeta, 'backendKey'), 'name');

  const objectType: string = kebabCase(objectTypeMeta[to.params.objectTypeRaw].namePlural);

  const oldPage: string | undefined = to.query['page'];
  const oldPageSize: string | undefined = to.query['page_size'];
  const oldFields: string | undefined = to.query['fields'];
  const oldSort: string | undefined = to.query['sort'];
  const oldFilter: string | undefined = to.query['filter'];
  const oldFavorite: string | undefined = to.query['bookmark'];

  const pageSize: number | undefined = oldPageSize && translateOldPageSize(oldPageSize);
  const fields: string[] | undefined = oldFields && translateOldFields(oldFields, backendKeyToName);
  const sort: object | undefined = oldSort && translateOldSort(oldSort, backendKeyToName);
  const filter: object | undefined = oldFilter && translateOldFilter(oldFilter, backendKeyToName);
  const favorite: string | undefined = oldFavorite && translateOldFavorite(oldFavorite);

  const newSyntax = {
    objectType,

    // State only in url
    page: oldPage,
    bookmark: favorite,

    // User preferences
    pageSize,
    columns: fields,
    sort,
    filters: filter,
  };

  return newSyntax;
}

function translateOldPageSize(oldPageSize: string): number | undefined {
  if (!toNumber(oldPageSize)) return undefined;
  if (includes(PAGE_SIZES, toNumber(oldPageSize))) return toNumber(oldPageSize);
  return PAGE_SIZES[0];
}

/**
 * Translates fields syntax from old to new, i.e.:
 * From "slug,title,program_owner,__custom__:1,__mapping__:assessment,__mapping__:org group"
 * to ["slug", "title", "programOwners", "ca__2", "mappings.assessments", "mappings.orgGroups"]
 *
 * Also skips no longer existing fields.
 */
function translateOldFields(oldFieldsParam: string, backendKeyToName): string[] | undefined {
  const oldFields = oldFieldsParam.split(',');

  // Maps old field names to new fields names, adds undefined to array if fields doesn't exist (i.e. for CAs)
  let newFields = map(oldFields, field => backendKeyToName[field]);
  // Removes undefined from array
  newFields = without(newFields, undefined);

  if (newFields.length == 0) return undefined;

  return newFields;
}

/**
 * Translates sort syntax from old to new, i.e.:
 * from "ascending:description" to {"f": "description","d": "asc"}
 */
function translateOldSort(oldSortParam: string, backendKeyToName): object | undefined {
  const oldSort = oldSortParam.split(':');

  if (oldSort.length != 2) return undefined;

  const oldSortDirection = oldSort[0];
  const oldSortField = oldSort[1];

  const directionOldToNew = {
    ascending: ColumnSortDirection.Asc,
    descending: ColumnSortDirection.Desc,
  };

  const newSort = {
    by: backendKeyToName[oldSortField],
    direction: directionOldToNew[oldSortDirection],
  };

  if (!newSort.by || !newSort.direction) return undefined;

  return newSort;
}

/**
 * Translates filters syntax from old to new, i.e.:
 * from { "title": { "op": "~", "value": "Title 1" } }
 * to { "f": "title", "v": "~", "o": "Title 1" }
 */
function translateOldFilter(oldFilterParam: string, backendKeyToName): object | undefined {
  let oldFilters: object;

  try {
    oldFilters = JSON.parse(oldFilterParam);
  } catch (e) {
    return undefined;
  }

  if (!isObject(oldFilters)) return undefined;

  let newFilters = map(oldFilters, (opAndValue, oldField) => {
    let op = opAndValue.op;
    let value = opAndValue.value;

    if (op === 'd=') op = '=';
    if (op === 'd<') op = '<';
    if (op === 'd>') op = '>';

    if (op === 'rel_equals' && (value === 'null' || value === '')) {
      op = 'IS_EMPTY';
      value = 'EMPTY';
    }
    if (op === 'rel_equals' && value !== 'null' && value !== '') {
      op = '~';
    }

    if (op === 'mapped') {
      op = 'MAPPED';
    }
    if (op === 'no_mapped') {
      op = 'NO_MAPPED';
    }

    if (!backendKeyToName[oldField] || !op || !value) return undefined;

    return {
      f: backendKeyToName[oldField],
      o: op,
      v: value,
    };
  });

  // Removes undefined from array
  newFilters = without(newFilters, undefined);

  return newFilters;
}

function translateOldFavorite(oldFavoriteParam: string): string | undefined {
  let oldFavorite: object;

  try {
    oldFavorite = JSON.parse(oldFavoriteParam);
  } catch (e) {
    return undefined;
  }

  if (!isObject(oldFavorite) || !isNumber(oldFavorite.id) || !isString(oldFavorite.title)) {
    return undefined;
  }

  return oldFavoriteParam;
}
